import { useState, useRef, useEffect } from "react";
import { Upload, Image as ImageIcon, X, Trash2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";

const inputCls = "w-full border border-tertiary bg-white px-3 py-2 text-sm outline-none focus:border-champagne focus:ring-1 focus:ring-champagne";

export default function ImageUpload({ value, onChange, label = "Image", testid = "image" }) {
  const [uploading, setUploading] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const fileRef = useRef();

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onChange(data.url);
      toast.success("Image téléversée");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Échec du téléversement");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  return (
    <div>
      <label className="mb-1 block text-xs uppercase tracking-[0.12em] text-anthracite/50">{label}</label>
      {value ? (
        <div className="relative mb-2 inline-block">
          <img src={value} alt="" className="h-24 w-40 border border-tertiary object-cover" />
          <button type="button" onClick={() => onChange("")} className="absolute -right-2 -top-2 rounded-full bg-anthracite p-1 text-ivory" aria-label="Retirer">
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      ) : (
        <div className="mb-2 flex h-24 w-40 items-center justify-center border border-dashed border-tertiary bg-sand text-anthracite/30">
          <ImageIcon className="h-6 w-6" strokeWidth={1.5} />
        </div>
      )}
      <div className="flex flex-wrap items-center gap-2">
        <input ref={fileRef} type="file" accept="image/*" onChange={handleFile} className="hidden" data-testid={`${testid}-file`} />
        <button type="button" onClick={() => fileRef.current?.click()} disabled={uploading} data-testid={`${testid}-upload`} className="inline-flex items-center gap-2 border border-anthracite px-3 py-1.5 text-xs text-anthracite transition-colors hover:bg-anthracite hover:text-ivory disabled:opacity-50">
          {uploading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />} Téléverser
        </button>
        <button type="button" onClick={() => setPickerOpen(true)} data-testid={`${testid}-library`} className="inline-flex items-center gap-2 border border-tertiary px-3 py-1.5 text-xs text-anthracite/70 hover:border-champagne">
          <ImageIcon className="h-3.5 w-3.5" /> Médiathèque
        </button>
      </div>
      <input value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder="ou coller une URL…" className={`${inputCls} mt-2`} data-testid={`${testid}-url`} />

      {pickerOpen && <MediaPicker onClose={() => setPickerOpen(false)} onPick={(url) => { onChange(url); setPickerOpen(false); }} />}
    </div>
  );
}

function MediaPicker({ onClose, onPick }) {
  const [media, setMedia] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = () => { setLoading(true); api.get("/admin/media").then(({ data }) => setMedia(data)).catch(() => {}).finally(() => setLoading(false)); };
  useEffect(() => { load(); }, []);

  const del = async (id) => { await api.delete(`/admin/media/${id}`); load(); };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-anthracite/50 p-4" data-testid="media-picker">
      <div className="max-h-[80vh] w-full max-w-3xl overflow-y-auto bg-white p-6">
        <div className="mb-4 flex items-center justify-between">
          <h3 className="font-serif text-xl text-anthracite">Médiathèque</h3>
          <button onClick={onClose}><X className="h-5 w-5" /></button>
        </div>
        {loading ? <p className="py-12 text-center text-anthracite/40">…</p> : media.length === 0 ? (
          <p className="py-12 text-center text-sm text-anthracite/40">Aucune image. Téléversez-en depuis n'importe quel formulaire.</p>
        ) : (
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            {media.map((m) => (
              <div key={m.id} className="group relative border border-tertiary">
                <img src={m.url} alt={m.original_filename} className="aspect-square w-full cursor-pointer object-cover" onClick={() => onPick(m.url)} data-testid={`media-pick-${m.id}`} />
                <button onClick={() => del(m.id)} className="absolute right-1 top-1 rounded bg-anthracite/70 p-1 text-ivory opacity-0 transition-opacity group-hover:opacity-100" aria-label="Supprimer">
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
